import { useCallback, useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import config from "@/config";
import { buildStatsQueryParams } from "@/lib/dashboard-api";
import type { DashboardStatsQuery } from "@/types";

type DryRunResponse = {
  dry_run: true;
  eligible_count: number;
  eligible_document_ids: string[];
  skipped: { document_id: string; detail?: string }[];
  truncated_export?: boolean;
};

type RunResponse = {
  dry_run: false;
  processed: number;
  summary: { completed: number; skipped: number; errors: number };
  results: Record<string, unknown>[];
};

async function readErrorMessage(res: Response): Promise<string> {
  const raw = await res.text();
  try {
    const body = JSON.parse(raw) as { detail?: unknown };
    if (typeof body.detail === "string") return body.detail;
  } catch {
    /* not JSON */
  }
  return raw || `HTTP ${res.status}`;
}

export function GeminiRerunDialog({
  statsQuery,
  onAfterRun,
}: {
  statsQuery: DashboardStatsQuery;
  onAfterRun?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dryResult, setDryResult] = useState<DryRunResponse | null>(null);
  const [runResult, setRunResult] = useState<RunResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const qs = buildStatsQueryParams(statsQuery).toString();
  const baseUrl = `${config.API_BASE_URL}/api/dashboard/rerun-gemini-structured-output${qs ? `?${qs}` : ""}`;

  const handlePreview = useCallback(async () => {
    setBusy(true);
    setError(null);
    setRunResult(null);
    try {
      const res = await fetch(baseUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dry_run: true }),
      });
      if (!res.ok) throw new Error(await readErrorMessage(res));
      const data = (await res.json()) as DryRunResponse;
      setDryResult(data);
    } catch (e) {
      setDryResult(null);
      setError(e instanceof Error ? e.message : "Preview failed");
    } finally {
      setBusy(false);
    }
  }, [baseUrl]);

  const handleRun = useCallback(async () => {
    if (!dryResult || dryResult.eligible_count === 0) return;
    const ok = window.confirm(
      `Re-run Gemini structured output for ${dryResult.eligible_count} case(s)? This calls the Gemini API for each eligible document.`,
    );
    if (!ok) return;

    setBusy(true);
    setError(null);
    setRunResult(null);
    try {
      const res = await fetch(baseUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dry_run: false }),
      });
      if (!res.ok) throw new Error(await readErrorMessage(res));
      const data = (await res.json()) as RunResponse;
      setRunResult(data);
      onAfterRun?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Run failed");
    } finally {
      setBusy(false);
    }
  }, [baseUrl, dryResult, onAfterRun]);

  const filterHint = [
    statsQuery.product && `product starts with "${statsQuery.product}"`,
    statsQuery.bin && `BIN ${statsQuery.bin}`,
    statsQuery.pcn && `PCN ${statsQuery.pcn}`,
    statsQuery.from && statsQuery.to && `created ${statsQuery.from}–${statsQuery.to}`,
    statsQuery.from && !statsQuery.to && `created from ${statsQuery.from}`,
    !statsQuery.from && statsQuery.to && `created until ${statsQuery.to}`,
  ]
    .filter(Boolean)
    .join("; ");

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setDryResult(null);
          setRunResult(null);
          setError(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1">
          <Sparkles className="h-3.5 w-3.5" />
          Rerun SO
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Rerun Gemini structured output</DialogTitle>
          <DialogDescription>
            For cases whose latest attempt failed with transcript processing unavailable (for example Gemini 503),
            re-run the model on the stored call transcript and update structured outputs. Uses the same case filters
            as this page (product, BIN, PCN, date range).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 text-sm text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">Filters:</span>{" "}
            {filterHint || "None (same unfiltered export slice as CSV export — consider narrowing)."}
          </p>
          {dryResult?.truncated_export ? (
            <p className="text-amber-700 dark:text-amber-400">
              Export list was truncated at the server cap. Add filters to cover all affected cases if needed.
            </p>
          ) : null}
        </div>

        {error ? (
          <p className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        ) : null}

        {dryResult ? (
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
            <p className="font-medium text-foreground">
              Eligible: {dryResult.eligible_count} case(s)
            </p>
            {dryResult.skipped.length > 0 ? (
              <p className="mt-1 text-muted-foreground">
                Skipped in preview: {dryResult.skipped.length} (not found or no longer eligible)
              </p>
            ) : null}
          </div>
        ) : null}

        {runResult ? (
          <div className="rounded-md border border-green-600/30 bg-green-600/10 px-3 py-2 text-sm">
            <p className="font-medium text-foreground">Finished</p>
            <p className="text-muted-foreground">
              Completed pipeline: {runResult.summary.completed}, skipped: {runResult.summary.skipped}, errors:{" "}
              {runResult.summary.errors}
            </p>
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="secondary" onClick={() => void handlePreview()} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Preview eligible
          </Button>
          <Button
            type="button"
            onClick={() => void handleRun()}
            disabled={busy || !dryResult || dryResult.eligible_count === 0}
          >
            Run for eligible cases
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
